/* ==========================================================================
   Crocs home page behaviour
   - <crocs-tabs>  : Explore Sales category tabs
   - <crocs-rail>  : horizontal rails — drag to scroll, optional prev/next
                     arrows, and optional one-card-per-swipe paging
   Both are progressive enhancements; the markup works without JS.
   ========================================================================== */

if (!customElements.get('crocs-tabs')) {
  customElements.define(
    'crocs-tabs',
    class CrocsTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (!this.tabs.length) return;

        this.addEventListener('click', (evt) => {
          const tab = evt.target.closest('[role="tab"]');
          if (!tab || !this.tabs.includes(tab)) return;
          evt.preventDefault();
          this.select(tab);
        });

        this.addEventListener('keydown', (evt) => {
          if (evt.key !== 'ArrowRight' && evt.key !== 'ArrowLeft') return;
          const current = this.tabs.indexOf(document.activeElement);
          if (current === -1) return;
          evt.preventDefault();
          const step = evt.key === 'ArrowRight' ? 1 : -1;
          const next = this.tabs[(current + step + this.tabs.length) % this.tabs.length];
          next.focus();
          this.select(next);
        });
      }

      select(tab) {
        this.tabs.forEach((el) => {
          el.setAttribute('aria-selected', el === tab ? 'true' : 'false');
          el.setAttribute('tabindex', el === tab ? '0' : '-1');
        });
        const target = tab.getAttribute('aria-controls');
        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== target;
        });
        /* A rail inside a hidden panel measures zero overflow, so it hides its
           arrows. Re-measure the rails now that the panel is on screen. */
        this.querySelectorAll('crocs-rail').forEach((rail) => {
          if (typeof rail.refresh === 'function') rail.refresh();
        });
      }
    }
  );
}

if (!customElements.get('crocs-rail')) {
  customElements.define(
    'crocs-rail',
    class CrocsRail extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.chx-rail') || this;
        /* Card-by-card paging is opt-in: it suits the product rail, but would
           feel sticky on the small category tiles. */
        this.paged = this.hasAttribute('data-snap');
        /* A mouse or trackpad scrolls freely; card-by-card paging is kept for
           touch, where a swipe is a discrete gesture rather than a scroll. */
        this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');

        if (this.paged) {
          /* pan-y stops the browser flinging the rail horizontally, so there
             is no momentum left running for goTo() to fight. */
          this.rail.style.touchAction = 'pan-y';
        }

        /* JS owns the snapping from here. Leaving CSS snap on made a paged
           rail jump twice per gesture, and it would tug a freely dragged rail
           back to the nearest card the moment it came to rest. The CSS rules
           stay in the stylesheet as the no-JS fallback. */
        this.applySnap = () => {
          this.rail.style.scrollSnapType = this.paged || this.fine.matches ? 'none' : '';
        };
        this.applySnap();
        this.fine.addEventListener('change', this.applySnap);

        this.initArrows();
        this.initGestures();
        this.initMeasure();
      }

      disconnectedCallback() {
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onLoad) window.removeEventListener('load', this.onLoad);
        if (this.railObserver) this.railObserver.disconnect();
        if (this.applySnap) this.fine.removeEventListener('change', this.applySnap);
        cancelAnimationFrame(this.glideFrame);
      }

      /** Re-measure the rail — call after it changes size or becomes visible. */
      refresh() {
        this.sync();
      }

      /* ---------------------------------------------------------- geometry */

      isRtl() {
        return getComputedStyle(this.rail).direction === 'rtl';
      }

      /** Distance of one card, including the gap that follows it. */
      step() {
        const card = this.rail.firstElementChild;
        if (!card) return this.rail.clientWidth || 1;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        return Math.max(1, card.getBoundingClientRect().width + gap);
      }

      maxScroll() {
        return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      }

      /** RTL reports scrollLeft as negative, so work in absolute distance. */
      position() {
        return Math.abs(this.rail.scrollLeft);
      }

      index() {
        return Math.round(this.position() / this.step());
      }

      lastIndex() {
        return Math.max(0, Math.ceil(this.maxScroll() / this.step()));
      }

      /** Scroll so card `i` sits against the start edge of the rail. */
      goTo(i, behavior) {
        /* an arrow press takes over from a glide, and hands the inline
           scroll-behavior back to the stylesheet */
        cancelAnimationFrame(this.glideFrame);
        this.rail.style.scrollBehavior = '';
        const target = Math.min(Math.max(i, 0), this.lastIndex()) * this.step();
        const left = Math.min(target, this.maxScroll()) * (this.isRtl() ? -1 : 1);
        /* Mark the scroll as ours so the settle pass does not treat the
           resulting scroll events as drift and nudge the rail a second time. */
        this.programmatic = true;
        clearTimeout(this.programmaticTimer);
        this.programmaticTimer = setTimeout(() => {
          this.programmatic = false;
        }, 700);
        this.rail.scrollTo({ left, behavior: behavior || 'smooth' });
      }

      /* ------------------------------------------------------------ arrows */

      initArrows() {
        this.prevBtn = this.querySelector('.chx-railnav--prev');
        this.nextBtn = this.querySelector('.chx-railnav--next');
        if (!this.prevBtn && !this.nextBtn) return;

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index() - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index() + 1));
      }

      /* --------------------------------------------------------- measuring */

      /* Runs on every rail, arrows or not: a rail without arrows still needs
         to know whether it can scroll, so it can offer the grab cursor. */
      initMeasure() {
        this.sync = this.sync.bind(this);
        this.onResize = this.sync;
        this.onLoad = this.sync;
        this.rail.addEventListener('scroll', this.sync, { passive: true });
        window.addEventListener('resize', this.onResize);
        /* card widths depend on images, so re-check once they have loaded */
        window.addEventListener('load', this.onLoad);
        /* A rail that starts inside a hidden tab panel measures zero overflow
           and would keep its arrows hidden for good; the observer fires when
           it gains size, and again whenever the cards reflow. */
        if ('ResizeObserver' in window) {
          this.railObserver = new ResizeObserver(() => this.sync());
          this.railObserver.observe(this.rail);
        }
        this.sync();
      }

      sync() {
        const max = this.maxScroll();
        const pos = this.position();
        const scrollable = max > 1;
        this.classList.toggle('chx-railwrap--static', !scrollable);
        /* The scroll hint retires once the rail has really been moved. The
           threshold is a fraction of a card because the browser's own snap
           can leave a rail resting a few pixels in from the start. */
        this.classList.toggle('chx-railwrap--scrolled', pos > Math.max(40, this.step() * 0.4));
        /* the grab cursor is only honest when there is somewhere to go */
        this.rail.classList.toggle('chx-rail--draggable', scrollable);
        if (this.prevBtn) this.prevBtn.disabled = pos <= 1;
        if (this.nextBtn) this.nextBtn.disabled = pos >= max - 1;
      }

      /* ---------------------------------------------------------- gestures */

      /** How far a gesture must travel before it counts as a swipe. */
      threshold() {
        return Math.min(60, this.step() * 0.18);
      }

      /**
       * One path for mouse and touch. On a paged rail `touch-action: pan-y`
       * keeps the browser out of the horizontal axis, so pointer events are
       * not cancelled mid-pan and the drag can simply follow the finger.
       */
      initGestures() {
        const rail = this.rail;

        /* Cards are links wrapped around images, so a mouse drag starts the
           browser's own drag-and-drop: it fires pointercancel one move in and
           the rail freezes. The rail owns the horizontal axis, so opt the
           content out of native dragging. */
        rail.addEventListener('dragstart', (evt) => evt.preventDefault());

        rail.addEventListener('pointerdown', (evt) => {
          if (evt.pointerType === 'mouse' && evt.button !== 0) return;
          /* non-paged rails keep native touch scrolling; only mouse is dragged */
          if (!this.paged && evt.pointerType !== 'mouse') return;
          cancelAnimationFrame(this.glideFrame);
          this.drag = {
            x: evt.clientX,
            y: evt.clientY,
            lastX: evt.clientX,
            lastT: evt.timeStamp,
            velocity: 0,
            free: evt.pointerType === 'mouse',
            left: rail.scrollLeft,
            index: this.index(),
            axis: null,
            moved: false,
          };
          rail.style.scrollBehavior = 'auto';
        });

        rail.addEventListener('pointermove', (evt) => {
          const d = this.drag;
          if (!d) return;
          const dx = evt.clientX - d.x;
          const dy = evt.clientY - d.y;
          /* decide once whether this gesture is horizontal or a page scroll */
          if (!d.axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
            d.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          }
          if (d.axis !== 'x') return;
          if (!d.moved) rail.classList.add('chx-rail--dragging');
          d.moved = true;
          /* Smoothed pointer speed in px per millisecond, so a slow drag
             glides a little and a flick glides a lot — measuring per event
             instead would make a slow, coarse drag look just as fast. */
          const dt = Math.max(8, evt.timeStamp - d.lastT);
          d.velocity = d.velocity * 0.6 + ((evt.clientX - d.lastX) / dt) * 0.4;
          d.lastX = evt.clientX;
          d.lastT = evt.timeStamp;
          rail.scrollLeft = d.left - dx;
        });

        const release = (evt) => {
          const d = this.drag;
          if (!d) return;
          this.drag = null;
          rail.classList.remove('chx-rail--dragging');
          this.suppressClick = d.moved;

          /* A mouse drag simply carries on and slows down; only touch snaps
             to the next card. */
          if (d.free) {
            /* a drag that paused before the release is not a flick */
            const stale = evt && evt.timeStamp - d.lastT > 90;
            if (d.axis === 'x' && d.moved && !stale) this.glide(d.velocity);
            else rail.style.scrollBehavior = '';
            return;
          }

          rail.style.scrollBehavior = '';
          if (!this.paged || d.axis !== 'x') return;

          const dx = (evt && typeof evt.clientX === 'number' ? evt.clientX : d.x) - d.x;
          if (Math.abs(dx) >= this.threshold()) {
            const forward = this.isRtl() ? dx > 0 : dx < 0;
            this.goTo(d.index + (forward ? 1 : -1));
          } else {
            this.goTo(d.index);
          }
        };

        rail.addEventListener('pointerup', release);
        rail.addEventListener('pointercancel', release);
        rail.addEventListener('pointerleave', release);

        /* Swallow the click that ends a drag so cards do not navigate. */
        rail.addEventListener(
          'click',
          (evt) => {
            if (this.suppressClick) {
              evt.preventDefault();
              evt.stopPropagation();
              this.suppressClick = false;
            }
          },
          true
        );

        if (!this.paged) return;

        /* Catch anything that leaves the rail off-centre without a gesture —
           trackpad, shift-wheel, focus jumps. */
        let settle;
        rail.addEventListener(
          'scroll',
          () => {
            if (this.drag || this.programmatic) return;
            clearTimeout(settle);
            settle = setTimeout(() => this.alignToNearest(), 140);
          },
          { passive: true }
        );
      }

      /* ---------------------------------------------------------- momentum */

      /**
       * Carry the rail on after the pointer lifts, shedding speed each frame,
       * so a flick coasts to a stop instead of stopping dead.
       */
      glide(pxPerMs) {
        cancelAnimationFrame(this.glideFrame);
        const rail = this.rail;
        /* scroll-behavior: smooth would animate every frame of this against
           itself; the glide is the animation. */
        rail.style.scrollBehavior = 'auto';
        /* per frame, and capped so a violent flick still lands somewhere the
           eye can follow */
        let v = Math.max(-60, Math.min(60, pxPerMs * 16.7));
        const stop = () => {
          rail.style.scrollBehavior = '';
        };
        const step = () => {
          v *= 0.93;
          if (Math.abs(v) < 0.5) return stop();
          const before = rail.scrollLeft;
          rail.scrollLeft = before - v;
          /* nothing moved: an end of the rail, so there is no glide left */
          if (Math.abs(rail.scrollLeft - before) < 0.5) return stop();
          this.glideFrame = requestAnimationFrame(step);
        };
        this.glideFrame = requestAnimationFrame(step);
      }

      alignToNearest() {
        if (this.drag || this.programmatic || this.fine.matches) return;
        const step = this.step();
        const pos = this.position();
        /* resting against either end is a legitimate position */
        if (pos <= 1 || pos >= this.maxScroll() - 1) return;
        const offset = pos % step;
        if (offset < 4 || step - offset < 4) return;
        this.goTo(Math.round(pos / step));
      }
    }
  );
}
